import React from 'react';

import { getRouteApi } from '@tanstack/react-router';
import { ALPHA_2_ISO_3166, EMPLOYMENT_INDUSTRY, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';

import TitleCombobox from '~/features/leadDetails/components/Overview/CustomerData/Employment/inputs/general/TitleCombobox.tsx';
import TitleComposedInput from '~/features/leadDetails/components/Overview/CustomerData/Employment/inputs/general/TitleComposedInput.tsx';
import TitleCurrencyInput from '~/features/leadDetails/components/Overview/CustomerData/Employment/inputs/general/TitleCurrencyInput.tsx';
import TitleDateInput from '~/features/leadDetails/components/Overview/CustomerData/Employment/inputs/general/TitleDateInput.tsx';
import TitleInput from '~/features/leadDetails/components/Overview/CustomerData/Employment/inputs/general/TitleInput.tsx';
import ProbationaryPeriod from '~/features/leadDetails/components/Overview/CustomerData/Employment/inputs/ProbationaryPeriod.tsx';
import { FormCard } from '~/features/leadDetails/components/Overview/CustomerData/shared/FormCard';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { canExecuteApplicantCommand } from '~/features/leadDetails/utils/aclPermissions';

const Official: React.FC<{ secondApplicant?: boolean }> = ({ secondApplicant }) => {
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { t } = useTranslation();
    const employmentTranslationPath = 'CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT';
    const incomeTranslationPath = 'CREDIT_WORTHINESS.SECTIONS.INCOME';

    const paths = {
        branch: 'beschaeftigung.beamter.beschaeftigungsverhaeltnis.arbeitgeber.branche',
        cumulativeAnnualValue: 'beschaeftigung.beamter.beschaeftigungsverhaeltnis.kumulierterJahreswert',
        employedSince: 'beschaeftigung.beamter.beschaeftigungsverhaeltnis.beschaeftigtSeit',
        employerCity: 'beschaeftigung.beamter.beschaeftigungsverhaeltnis.arbeitgeber.anschrift.ort',
        employerHouseNumber: 'beschaeftigung.beamter.beschaeftigungsverhaeltnis.arbeitgeber.anschrift.hausnummer',
        employerLand: 'beschaeftigung.beamter.beschaeftigungsverhaeltnis.arbeitgeber.anschrift.land',
        employerName: 'beschaeftigung.beamter.beschaeftigungsverhaeltnis.arbeitgeber.name',
        employerStreet: 'beschaeftigung.beamter.beschaeftigungsverhaeltnis.arbeitgeber.anschrift.strasse',
        employerZipCode: 'beschaeftigung.beamter.beschaeftigungsverhaeltnis.arbeitgeber.anschrift.plz',
        incomeMainActivity: 'beschaeftigung.beamter.beschaeftigungsverhaeltnis.nettoeinkommenMonatlich',
        incomeSecondLastMonth: 'beschaeftigung.beamter.beschaeftigungsverhaeltnis.nettoeinkommenVorletzterMonat',
        incomeSecondToLastMonth: 'beschaeftigung.beamter.beschaeftigungsverhaeltnis.nettoeinkommenVorVorletzterMonat',
        jobTitle: 'beschaeftigung.beamter.beschaeftigungsverhaeltnis.berufsbezeichnung',
        officialSince: 'beschaeftigung.beamter.beschaeftigungsverhaeltnis.verbeamtetSeit',
        probationaryPeriod: 'beschaeftigung.beamter.beschaeftigungsverhaeltnis.inProbezeit',
        probationaryPeriodEnd: 'beschaeftigung.beamter.beschaeftigungsverhaeltnis.inProbezeitBis',

        // contribution: 'beschaeftigung.beamter.beschaeftigungsverhaeltnis.krankenversicherungsbeitrag', // TODO
        // healthInsurance: 'beschaeftigung.beamter.beschaeftigungsverhaeltnis.krankenversicherung', // TODO
    };

    const isDisabled = !canExecuteApplicantCommand({
        applicant: secondApplicant ? 2 : 1,
        commandName: LOAN_REQUEST_COMMANDS.changeApplicantEmployment,
        permissions,
    });

    return (
        <>
            <FormCard data-testid={'official-sub-form'}>
                <TitleCombobox
                    title={t(`${employmentTranslationPath}.BRANCH`)}
                    name={paths.branch}
                    translationPath={'INDUSTRY'}
                    values={Object.values(EMPLOYMENT_INDUSTRY)}
                    disabled={isDisabled}
                />
                <TitleInput
                    title={t(`${employmentTranslationPath}.EMPLOYMENT_TYPE`)}
                    name={paths.jobTitle}
                    disabled={isDisabled}
                />
                <TitleDateInput
                    title={t(`${employmentTranslationPath}.EMPLOYED_SINCE`)}
                    name={paths.employedSince}
                    disabled={isDisabled}
                />
                <TitleDateInput
                    title={t(`${employmentTranslationPath}.CIVIL_SERVANT_SINCE`)}
                    name={paths.officialSince}
                    disabled={isDisabled}
                />
            </FormCard>
            <FormCard title={t(`${employmentTranslationPath}.SUBSECTIONS.INCOME_HEALTH_INSURANCE`)}>
                <TitleCurrencyInput
                    title={t(`${incomeTranslationPath}.MAIN_INCOME`)}
                    name={paths.incomeMainActivity}
                    disabled={isDisabled}
                />
                <TitleCurrencyInput
                    title={t(`${incomeTranslationPath}.SALARY_LAST_MONTH`)}
                    name={paths.incomeSecondLastMonth}
                    disabled={isDisabled}
                />
                <TitleCurrencyInput
                    title={t(`${incomeTranslationPath}.SALARY_BEFORE_LAST_MONTH`)}
                    name={paths.incomeSecondToLastMonth}
                    disabled={isDisabled}
                />
                <TitleCurrencyInput
                    title={t(`${incomeTranslationPath}.CUMULATIVE_ANNUAL_VALUE`)}
                    name={paths.cumulativeAnnualValue}
                    disabled={isDisabled}
                />
                {/* <HealthInsurance healthInsurancePath={paths.healthInsurance} contributionPath={paths.contribution} /> */}
            </FormCard>
            <FormCard title={t(`${employmentTranslationPath}.SUBSECTIONS.LIMITATIONS`)}>
                <ProbationaryPeriod
                    probationaryPeriodPath={paths.probationaryPeriod}
                    probationaryPeriodEndPath={paths.probationaryPeriodEnd}
                    disabled={isDisabled}
                />
            </FormCard>
            <FormCard title={t(`${employmentTranslationPath}.SUBSECTIONS.EMPLOYER`)}>
                <TitleInput
                    title={t(`${employmentTranslationPath}.EMPLOYER_NAME`)}
                    name={paths.employerName}
                    placeholder={t(`${employmentTranslationPath}.EMPLOYER_NAME`)}
                    disabled={isDisabled}
                />
                <TitleComposedInput
                    firstTitle={t('ADDRESS.STREET')}
                    secondTitle={t('ADDRESS.HOUSE_NUMBER')}
                    firstInputName={paths.employerStreet}
                    secondInputName={paths.employerHouseNumber}
                    firstPlaceholder={t('ADDRESS.STREET')}
                    secondPlaceholder={t('ADDRESS.HOUSE_NUMBER_SHORT')}
                    disabled={isDisabled}
                />
                <TitleComposedInput
                    firstTitle={t('ADDRESS.ZIP_CODE')}
                    secondTitle={t('ADDRESS.LOCATION')}
                    firstInputName={paths.employerZipCode}
                    secondInputName={paths.employerCity}
                    firstPlaceholder={t('ADDRESS.ZIP_CODE_SHORT')}
                    secondPlaceholder={t('ADDRESS.LOCATION')}
                    firstKeepDigits={5}
                    swapSize={true}
                    disabled={isDisabled}
                />
                <TitleCombobox
                    title={t('ADDRESS.COUNTRY')}
                    name={paths.employerLand}
                    translationPath={'countries'}
                    values={Object.values(ALPHA_2_ISO_3166)}
                    disabled={isDisabled}
                />
            </FormCard>
        </>
    );
};

export default Official;
